import { useEffect, useRef } from 'react'

interface RevealOptions {
  rootMargin?: string
  threshold?: number
}

/**
 * Reveal-on-scroll para as seções da landing. Pareia com o CSS
 * `[data-reveal-state]` em src/index.css: o container recebe o ref e os
 * itens internos `data-reveal-item` (stagger via .reveal-delay-* ou
 * --reveal-delay inline).
 *
 * Segurança por design: sem JS, sem IntersectionObserver ou com
 * prefers-reduced-motion, nenhum data-reveal-state é aplicado e o conteúdo
 * fica visível desde o primeiro paint. Elementos já visíveis no mount
 * (deep-link/refresh no meio da página) também não animam.
 */
export function useRevealOnScroll<T extends HTMLElement = HTMLDivElement>(
  options: RevealOptions = {}
) {
  const ref = useRef<T>(null)
  const { rootMargin = '0px 0px -10% 0px', threshold = 0.15 } = options

  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !('IntersectionObserver' in window)
    ) {
      return
    }

    const rect = el.getBoundingClientRect()
    if (rect.top < window.innerHeight && rect.bottom > 0) return

    el.dataset.revealState = 'pending'
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.dataset.revealState = 'revealed'
          io.disconnect()
        }
      },
      { rootMargin, threshold }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [rootMargin, threshold])

  return ref
}
